import { memo, useState, type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useDraggable } from '@dnd-kit/core'
import { Check, MoreHorizontal } from 'lucide-react'
import type { BoardCard } from '@/api/boardsApi'
import type { Agent } from '@/api/directoryApi'
import { AssigneeAvatar, AssigneeOptions, AssigneePicker } from '@/components/board/AssigneePicker'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { PRIORITY_COLUMNS, type MovePermissions } from '@/lib/boardColumns'
import { isAtRisk } from '@/lib/boardFilters'
import { SLA_TONE_CLASS, slaCell } from '@/lib/slaDisplay'
import { priorityDotClass, priorityLabel, statusLabel } from '@/lib/ticketLabels'
import { cn } from '@/lib/utils'

export type CardAction = { kind: 'status'; value: string } | { kind: 'priority'; value: string } | { kind: 'assignee'; value: string | null }
export type Density = 'comfortable' | 'compact'

/**
 * The card's face: key, SLA, subject, customer · product and who has it.
 * `assigneeSlot` leaves the avatar's place empty for the AssigneePicker that
 * DraggableCard lays over it (a button can't live inside the card's link).
 */
export function CardFace({
  card,
  now,
  density = 'comfortable',
  dragging,
  selected,
  assigneeSlot,
}: {
  card: BoardCard
  now: number
  density?: Density
  dragging?: boolean
  selected?: boolean
  assigneeSlot?: boolean
}) {
  const sla = slaCell(card.sla, card.status, now)
  const risk = isAtRisk(card, now)
  const size = density === 'compact' ? 'sm' : 'md'
  const avatar = assigneeSlot ? (
    <span className={cn('shrink-0', size === 'sm' ? 'size-5' : 'size-6')} aria-hidden />
  ) : card.assignee ? (
    <AssigneeAvatar person={card.assignee} size={size} />
  ) : null
  return (
    <div
      className={cn(
        'flex flex-col rounded-lg border bg-background shadow-sm transition-shadow hover:shadow-md',
        density === 'compact' ? 'gap-1 p-2.5' : 'gap-2 p-3',
        risk ? 'border-l-[3px] border-y-line border-r-line border-l-sla-critical' : 'border-line',
        selected && 'ring-2 ring-primary',
        dragging && 'rotate-1 shadow-xl ring-2 ring-primary/30',
      )}
    >
      <div className="flex items-center gap-2">
        <span className={cn('size-2 shrink-0 rounded-sm', priorityDotClass(card.priority))} aria-label={card.priority} />
        <span className="font-mono text-label text-ink-muted">{card.key}</span>
        <span className={cn('ml-auto font-mono text-label', SLA_TONE_CLASS[sla.tone])}>{sla.text}</span>
      </div>
      {density === 'compact' ? (
        <div className="flex items-center gap-2">
          <p className="line-clamp-1 min-w-0 grow text-label font-medium text-ink-strong">{card.subject}</p>
          {avatar}
        </div>
      ) : (
        <>
          <p className="line-clamp-2 text-meta font-medium text-ink-strong">{card.subject}</p>
          <div className="flex items-center gap-2">
            <span className="min-w-0 grow truncate text-label text-ink-muted">{[card.customer?.name, card.product?.name].filter(Boolean).join(' · ')}</span>
            {avatar}
          </div>
        </>
      )}
    </div>
  )
}

/**
 * A draggable card (pointer drags after 6 px, so a click still works). Click
 * opens the side panel; Cmd/Ctrl-click the full page. The checkbox selects for
 * bulk actions (Shift-click extends); the "…" menu is the no-drag path.
 */
export const DraggableCard = memo(function DraggableCard({
  card,
  now,
  agents,
  meId,
  can,
  density,
  selected,
  onAction,
  onOpen,
  onSelect,
}: {
  card: BoardCard
  now: number
  agents: Agent[]
  meId: string | null
  can: MovePermissions
  density: Density
  selected: boolean
  onAction: (card: BoardCard, action: CardAction) => void
  onOpen: (key: string) => void
  onSelect: (id: string, range: boolean) => void
}) {
  const { t } = useTranslation()
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: card.id, data: { card } })
  const [menuOpen, setMenuOpen] = useState(false)
  const act = (action: CardAction) => {
    setMenuOpen(false)
    onAction(card, action)
  }
  const open = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.metaKey || e.ctrlKey || e.button !== 0) return
    e.preventDefault()
    onOpen(card.key)
  }
  const moves = [...card.allowedTransitions, ...(can.resolve && card.status !== 'resolved' && card.status !== 'closed' ? ['resolved'] : [])]
  const item = 'block w-full rounded-sm px-2.5 py-1.5 text-left text-meta text-ink-body hover:bg-surface-subtle focus-visible:bg-surface-subtle focus-visible:outline-none'
  const heading = 'px-2.5 pb-1 pt-2 text-eyebrow font-medium uppercase text-ink-muted'

  return (
    <li ref={setNodeRef} data-flip-id={card.id} className={cn('group relative list-none', isDragging && 'opacity-30')} aria-label={`${card.key} ${card.subject}`}>
      <div {...attributes} {...listeners} className="cursor-grab touch-none rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:cursor-grabbing">
        <a href={`/tickets/${card.key}`} onClick={open} className="block rounded-lg" draggable={false}>
          <CardFace card={card} now={now} density={density} selected={selected} assigneeSlot />
        </a>
      </div>

      {/* Over the face's empty avatar slot: the card border (1 px) plus its padding. */}
      <div className={cn('absolute', density === 'compact' ? 'bottom-[11px] right-[11px]' : 'bottom-[13px] right-[13px]')}>
        <AssigneePicker
          ticketKey={card.key}
          assignee={card.assignee}
          agents={agents}
          meId={meId}
          canAssign={can.assign}
          size={density === 'compact' ? 'sm' : 'md'}
          onChange={(value) => onAction(card, { kind: 'assignee', value })}
        />
      </div>

      <button
        type="button"
        role="checkbox"
        aria-checked={selected}
        aria-label={t('boards.bulk.select', { key: card.key })}
        onClick={(e) => onSelect(card.id, e.shiftKey)}
        className={cn(
          'absolute -left-1.5 -top-1.5 flex size-5 items-center justify-center rounded-md border bg-background shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          selected ? 'border-primary bg-primary text-primary-foreground' : 'border-line-strong opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
        )}
      >
        {selected ? <Check className="size-3.5" aria-hidden /> : null}
      </button>

      <div className="absolute right-1.5 top-1.5 flex gap-1 opacity-0 group-hover:opacity-100 has-[[data-state=open]]:opacity-100 has-[:focus-visible]:opacity-100">
        <Popover open={menuOpen} onOpenChange={setMenuOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={t('boards.card.actions', { key: card.key })}
              className="rounded-md bg-background/90 p-1 text-ink-muted hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <MoreHorizontal className="size-4" aria-hidden />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="max-h-96 w-60 overflow-y-auto p-1">
            {can.update || can.resolve ? (
              <div role="group" aria-label={t('boards.card.moveTo')}>
                <p className={heading}>{t('boards.card.moveTo')}</p>
                {moves.map((s) => (
                  <button key={s} type="button" className={item} onClick={() => act({ kind: 'status', value: s })}>
                    {statusLabel(s)}
                  </button>
                ))}
              </div>
            ) : null}
            {can.update ? (
              <div role="group" aria-label={t('boards.card.priority')}>
                <p className={heading}>{t('boards.card.priority')}</p>
                {PRIORITY_COLUMNS.filter((p) => p !== card.priority.toUpperCase()).map((p) => (
                  <button key={p} type="button" className={item} onClick={() => act({ kind: 'priority', value: p })}>
                    {priorityLabel(p)}
                  </button>
                ))}
              </div>
            ) : null}
            {can.assign ? (
              <div role="group" aria-label={t('boards.card.assign')}>
                <p className={heading}>{t('boards.card.assign')}</p>
                <AssigneeOptions
                  assigneeId={card.assignee?.id ?? null}
                  agents={agents}
                  meId={meId}
                  onPick={(value) => (value === (card.assignee?.id ?? null) ? setMenuOpen(false) : act({ kind: 'assignee', value }))}
                />
              </div>
            ) : null}
          </PopoverContent>
        </Popover>
      </div>
    </li>
  )
})
